import { isHidden } from "@/lib/hidden-features";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import { useElementSize, useMediaQuery } from "@mantine/hooks";
import {
  BackgroundImage,
  Box,
  Button,
  Card,
  Flex,
  Group,
  ScrollArea,
  Select,
  Stack,
  Tabs,
  Text,
} from "@mantine/core";
import { useMantineColorScheme, useMantineTheme } from "@mantine/core";
import {
  CalendarBlankIcon,
  FilePlusIcon,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import VisitSummaryModal from "@/components/visit-summary-modal";


export default function EngagementCenterLayout() {
  const size = useElementSize();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { colorScheme } = useMantineColorScheme();
  const navigate = useNavigate();
  const theme = useMantineTheme();
  // Single scroll area height: mobile uses 100dvh (dynamic viewport) for address-bar resilience
  const scrollAreaHeight = isMobile ? "calc(100dvh - 220px)" : "calc(100vh - 340px)";

  const [activeTab, setActiveTab] = useState<string | null>("checklist");
  const [visitSummaryModalOpen, setVisitSummaryModalOpen] = useState(false);
  const tabRoutes: Record<string, string> = {
    checklist: "/app/engagement-center/",
    health: "/app/engagement-center/health-summary",
    bookmarks: "/app/engagement-center/bookmarks",
    knowledge: "/app/engagement-center/knowledge-check",
  };
  // Keep the pill in sync with the URL (deep links, the index redirect when the
  // checklist is hidden, browser back/forward).
  const location = useLocation();
  useEffect(() => {
    const here = location.pathname.replace(/\/$/, "");
    const tab = Object.entries(tabRoutes).find(([, r]) => r.replace(/\/$/, "") === here)?.[0];
    if (tab) setActiveTab(tab);
  }, [location.pathname]);
  const tabBackgrounds: Record<string, string> = {
    checklist: "/assets/home_background_image.jpg",
    health: "/assets/carousel-image3.jpg",
    bookmarks: "/assets/carousel-image2.jpg",
    knowledge: "/assets/In_Progress.jpg",
  };
  const backgroundPositions: Record<string, string> = {
    checklist: "center 40%",
    health: "center 45%",
    bookmarks: "center 50%",
    knowledge: "center 60%",
  };
  const backgroundImageSrc = tabBackgrounds[activeTab ?? "checklist"];
  const backgroundPosition = backgroundPositions[activeTab ?? "checklist"];

  return (
    <Box p={"md"}>
      <Card p={0} radius="md" withBorder>
        <BackgroundImage
          src={backgroundImageSrc}
          radius="md"
          style={{
            backgroundPosition: backgroundPosition,
            backgroundSize: "cover",
            height: 180,
            width: "100%",
          }}
        >
          <Box
            p="xl"
            style={{
              position: "absolute",
              bottom: 5,
              left: 0,
              right: 0,
              height: "100%",
              borderRadius: "inherit",
              zIndex: 1,
            }}
          >
            <Group justify="space-between" align="flex-start" h="100%">
              <Stack gap={4} style={{ marginTop: "80px" }}>
                <Text fw={700} size="xl" c="#FAFAFA">
                  Progress
                </Text>
                <Text size="md" c="#FAFAFA" fw={700}>
                  Personalized health hub for tracking progress and staying
                  engaged
                </Text>
              </Stack>

              {activeTab === "checklist" && (
                <Card
                  p="md"
                  radius="md"
                  bg={
                    colorScheme === "dark"
                      ? theme.colors.neutral[9]
                      : theme.colors.orange[1]
                  }
                  h={140}
                >
                  <Stack gap="sm">
                    <Box>
                      <Text
                        size="md"
                        fw={500}
                        mb={4}
                        c={theme.colors.indigo[6]}
                      >
                        Primary Condition
                      </Text>
                      <Text
                        size="md"
                        c={
                          colorScheme === "dark"
                            ? theme.colors.neutral[0]
                            : theme.colors.neutral[9]
                        }
                      >
                        Secondary Hypertension with reduced kidney function (GFR
                        = 38 mL/min)
                      </Text>
                    </Box>
                    <Box>
                      <Text
                        size="md"
                        fw={500}
                        mb={4}
                        c={theme.colors.indigo[6]}
                      >
                        Goal
                      </Text>
                      <Text
                        size="md"
                        c={
                          colorScheme === "dark"
                            ? theme.colors.neutral[0]
                            : theme.colors.neutral[9]
                        }
                      >
                        Empower consumer to manage health, prevent
                        complications, and improve well-being.
                      </Text>
                    </Box>
                  </Stack>
                </Card>
              )}

              {activeTab === "health" && (
                <Card
                  p="md"
                  radius="md"
                  bg={
                    colorScheme === "dark"
                      ? theme.colors.neutral[9]
                      : theme.colors.orange[1]
                  }
                  mt={80}
                >
                  <Group gap={"xs"}>
                    <CalendarBlankIcon size={24} />

                    <Text
                      c={
                        colorScheme === "dark"
                          ? theme.colors.neutral[0]
                          : theme.colors.neutral[9]
                      }
                    >
                      {" "}
                      Updated on 12 July , 2025
                    </Text>
                  </Group>
                </Card>
              )}

              {activeTab === "knowledge" && (
                <Card
                  p="md"
                  radius="md"
                  bg={
                    colorScheme === "dark"
                      ? theme.colors.neutral[9]
                      : theme.colors.orange[1]
                  }
                  mt={50}
                >
                  <Flex direction={"column"} gap={0}>
                    <Text c={theme.colors.indigo[6]} ta={"center"}>
                      100
                    </Text>
                    <Text
                      size="sm"
                      c={
                        colorScheme === "dark"
                          ? theme.colors.neutral[0]
                          : theme.colors.neutral[9]
                      }
                    >
                      {" "}
                      Total Points Earned{" "}
                    </Text>
                  </Flex>
                </Card>
              )}
            </Group>
          </Box>
        </BackgroundImage>
      </Card>

      <Tabs
        mt={"md"}
        value={activeTab}
        onChange={(tab) => {
          setActiveTab(tab);
          if (tab && tabRoutes[tab]) {
            navigate(tabRoutes[tab]);
          }
        }}
        radius="md"
        variant="pills"
        defaultValue="new"
      >
        <Group justify="space-between">
          <Group>
            <Tabs.List
              bg={
                colorScheme === "dark"
                  ? theme.colors.neutral[9]
                  : theme.colors.neutral[2]
              }
              style={{ borderRadius: "10px" }}
              p={"xs"}
            >
              {!isHidden("priorityChecklist") && (
              <Tabs.Tab 
                value="checklist"
                style={{
                  backgroundColor: activeTab === "checklist" ? theme.colors.primary[7] : "transparent",
                  color: activeTab === "checklist" ? "white" : undefined,
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== "checklist") {
                    e.currentTarget.style.backgroundColor = colorScheme === "dark" 
                      ? "#555555"
                      : "white";
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== "checklist") {
                    e.currentTarget.style.backgroundColor = "transparent";
                  } else {
                    e.currentTarget.style.backgroundColor = theme.colors.primary[7];
                  }
                }}
              >
                Health Priority Checklist 
              </Tabs.Tab>
              )}
              <Tabs.Tab 
                value="health"
                style={{
                  backgroundColor: activeTab === "health" ? theme.colors.primary[7] : "transparent",
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== "health") {
                    e.currentTarget.style.backgroundColor = colorScheme === "dark" 
                      ? "#555555"
                      : "white";
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== "health") {
                    e.currentTarget.style.backgroundColor = "transparent";
                  } else {
                    e.currentTarget.style.backgroundColor = theme.colors.primary[7];
                  }
                }}
              >
                Health Summary
              </Tabs.Tab>

              <Tabs.Tab 
                value="bookmarks"
                style={{
                  backgroundColor: activeTab === "bookmarks" ? theme.colors.primary[7] : "transparent",
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== "bookmarks") {
                    e.currentTarget.style.backgroundColor = colorScheme === "dark" 
                      ? "#555555"
                      : "white";
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== "bookmarks") {
                    e.currentTarget.style.backgroundColor = "transparent";
                  } else {
                    e.currentTarget.style.backgroundColor = theme.colors.primary[7];
                  }
                }}
              >
                Bookmarks
              </Tabs.Tab>
              {!isHidden("knowledgeCheck") && (
              <Tabs.Tab 
                value="knowledge"
                style={{
                  backgroundColor: activeTab === "knowledge" ? theme.colors.primary[7] : "transparent",
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== "knowledge") {
                    e.currentTarget.style.backgroundColor = colorScheme === "dark" 
                      ? "#555555"
                      : "white";
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== "knowledge") {
                    e.currentTarget.style.backgroundColor = "transparent";
                  } else {
                    e.currentTarget.style.backgroundColor = theme.colors.primary[7];
                  }
                }}
              >
                Knowledge Check
              </Tabs.Tab>
              )}
            </Tabs.List>
          </Group>

          {/* {(activeTab === "bookmarks" || activeTab === "knowledge") && (
            <TextInput
              placeholder="Search..."
              leftSection={<MagnifyingGlassIcon size={16} />}
              w="30%"
              size="md"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.currentTarget.value)}
            />
          )} */}
          {activeTab === "checklist" && !isHidden("visitSummary") && (
            <Button
              leftSection={<FilePlusIcon size={16} />}
              variant="light"
              color="indigo"
              size="md"
              radius="md"
              onClick={() => setVisitSummaryModalOpen(true)}
              styles={{
                root: {
                  backgroundColor: colorScheme === "dark" 
                    ? theme.colors.indigo[5] 
                    : theme.colors.indigo[1],
                  border: `1px solid ${colorScheme === "dark" 
                    ? theme.colors.indigo[6] 
                    : theme.colors.indigo[3]}`,
                  color: colorScheme === "dark" 
                    ? theme.colors.neutral[0] 
                    : theme.colors.indigo[9],
                  borderRadius: "8px",
                },
              }}
            >
              Create Visit Summary
            </Button>
          )}

          {activeTab === "health" && (
            <Select
              placeholder="All"
              data={[
                { value: "all", label: "All" },
                { value: "heart", label: "Heart" },
                { value: "diabetes", label: "Diabetes" },
                { value: "lifestyle", label: "Lifestyle" },
                { value: "other", label: "Other" },
              ]}
              defaultValue="all"
              // w={120}
              size="md"
              radius="md"
            />
          )}
        </Group>
      </Tabs>

      <Flex style={{ flexGrow: 1, minHeight: 0 }} direction="column" ref={size.ref}>
        <ScrollArea.Autosize h={scrollAreaHeight} style={{ minHeight: 200 }}>
          <Outlet />
        </ScrollArea.Autosize>
      </Flex>

      <VisitSummaryModal
        opened={visitSummaryModalOpen}
        onClose={() => setVisitSummaryModalOpen(false)}
      />
    </Box>
  );
}
